import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  useUserAnalytics, 
  useSplitAnalytics, 
  useTransactionAnalytics,
  useSharedWalletAnalytics,
  useBadgeAnalytics,
  usePhoneAuthAnalytics,
  useFinancialFlowAnalytics
} from '../../hooks/useAnalytics';
import KPICard from './core/KPICard';
import MetricSection from './core/MetricSection';
import TrendIndicator from './core/TrendIndicator';
import ReportExport from './ReportExport';
import { useFormatting } from '../../hooks/useFormatting';
import { formatPercent } from '../../utils/format';
import { calculateGrowth } from '../../utils/chartHelpers';
import { usePreviousPeriodData } from '../../hooks/usePreviousPeriodData';
import { FILTER_PERIODS } from '../../constants';

// Helper to get previous period for display
const getPreviousPeriodLabel = (currentPeriod) => {
  const periodMap = {
    [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
    [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
    [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
    [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
    [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
  };
  return periodMap[currentPeriod] || FILTER_PERIODS.DAY;
};

/**
 * Dashboard Overview Component
 * Main landing page for admin dashboard with trend indicators and period comparisons
 * Based on best practices from Stripe, Vercel, and Linear dashboards
 */
const DashboardOverview = ({ period, platform, compact = false }) => {
  // Always call hooks at top level (React rules)
  const { data: userData, loading: userLoading } = useUserAnalytics(period, platform);
  const { data: splitData, loading: splitLoading } = useSplitAnalytics(period);
  const { data: txData, loading: txLoading } = useTransactionAnalytics(period);
  const { data: walletData, loading: walletLoading } = useSharedWalletAnalytics(period);
  const { data: badgeData, loading: badgeLoading } = useBadgeAnalytics(period);
  const { data: phoneAuthData, loading: phoneAuthLoading } = usePhoneAuthAnalytics(period);
  const { data: financialFlow, loading: financialFlowLoading } = useFinancialFlowAnalytics(period);

  // Previous period data for trend calculations - using shared hook
  const { data: previousData, loading: previousLoading } = usePreviousPeriodData(
    ['users', 'splits', 'transactions', 'wallets', 'badges', 'phoneAuth'],
    period,
    platform
  );

  const { formatCurrency, formatNumber } = useFormatting();
  const isLoading = userLoading || splitLoading || txLoading || walletLoading || badgeLoading || phoneAuthLoading || financialFlowLoading || previousLoading;

  // Calculate trend for a metric
  const calculateTrend = (current, previous) => {
    if (!current || !previous) return null;
    return calculateGrowth(current, previous);
  };

  // All hooks must be called before any conditional returns (React rules)
  // Top-level KPIs with trend indicators - Reordered by importance
  const topKPIs = useMemo(() => [
    {
      id: 'total-users',
      title: 'Total Users',
      value: userData?.totalUserCount || 0,
      previousValue: previousData.users?.totalUserCount || null,
      formatType: 'number',
      icon: 'bi-people-fill',
      color: 'blue',
      metricId: 'total-users',
      subtitle: 'All time users',
      description: 'Total number of registered users since launch',
      loading: userLoading
    },
    {
      id: 'transaction-volume',
      title: 'Transaction Volume',
      value: txData?.totalTransactionValue || 0, // Only use actual transaction value, not requests or internal transfers
      previousValue: previousData.transactions?.totalTransactionValue || null,
      formatType: 'currency',
      icon: 'bi-cash-coin',
      color: 'green',
      metricId: 'transaction-volume',
      subtitle: 'Total value',
      description: 'Total dollar value of all actual transactions (mainnet only, excludes requests and internal transfers)',
      loading: txLoading
    },
    {
      id: 'total-splits',
      title: 'Total Splits',
      value: splitData?.totalSplits || 0,
      previousValue: previousData.splits?.totalSplits || null,
      formatType: 'number',
      icon: 'bi-scissors',
      color: 'purple',
      subtitle: `${period} period`,
      description: 'Total number of expense splits created by users',
      loading: splitLoading
    },
    {
      id: 'active-users',
      title: 'Active Users',
      value: userData?.walletUsers || 0,
      previousValue: previousData.users?.walletUsers || null,
      formatType: 'number',
      icon: 'bi-lightning-charge-fill',
      color: 'green',
      subtitle: 'Users with wallets',
      loading: userLoading,
      metricId: 'active-users'
    }
  ], [userData, splitData, txData, previousData, period, userLoading, splitLoading, txLoading]);

  // User Growth Metrics with trends - Reordered by importance
  const userMetrics = useMemo(() => [
    {
      id: 'new-users',
      title: 'New Users',
      value: userData?.periodUserCount || 0,
      previousValue: previousData.users?.periodUserCount || null,
      formatType: 'number',
      icon: 'bi-person-plus',
      color: 'green',
      subtitle: `${period} period`,
      metricId: 'new-users'
    },
    {
      id: 'verified-users',
      title: 'Verified Users',
      value: userData?.verifiedUsers || 0,
      previousValue: previousData.users?.verifiedUsers || null,
      formatType: 'number',
      icon: 'bi-patch-check',
      color: 'blue',
      subtitle: formatPercent(
        userData?.totalUserCount > 0 
          ? (userData.verifiedUsers / userData.totalUserCount) * 100 
          : 0, 
        1
      ),
      metricId: 'verified-users'
    },
    {
      id: 'onboarded-users',
      title: 'Onboarded Users',
      value: userData?.onboardedUsers || 0,
      previousValue: previousData.users?.onboardedUsers || null,
      formatType: 'number',
      icon: 'bi-rocket-takeoff',
      color: 'teal',
      subtitle: formatPercent(
        userData?.totalUserCount > 0 
          ? (userData.onboardedUsers / userData.totalUserCount) * 100 
          : 0, 
        1
      ),
      metricId: 'onboarded-users'
    },
    {
      id: 'phone-auth-users',
      title: 'Phone Auth Users',
      value: phoneAuthData?.usersWithPhone || 0,
      previousValue: previousData.phoneAuth?.usersWithPhone || null,
      formatType: 'number',
      icon: 'bi-phone',
      color: 'purple',
      subtitle: formatPercent(phoneAuthData?.phoneAuthAdoptionRate || 0, 1),
      metricId: 'phone-auth-users'
    }
  ], [userData, phoneAuthData, previousData, period]);

  // Financial Metrics with trends - Comprehensive separated data
  const financialMetrics = useMemo(() => [
    {
      id: 'splits-total',
      title: 'Splits Total',
      value: financialFlow?.splits?.totalValue || splitData?.totalAmount || 0,
      previousValue: previousData.splits?.totalAmount || null,
      formatType: 'currency',
      icon: 'bi-scissors',
      color: 'purple',
      subtitle: `${financialFlow?.splits?.count || splitData?.totalSplits || 0} splits`,
      metricId: 'split-amount'
    },
    {
      id: 'splits-completed',
      title: 'Completed Splits',
      value: financialFlow?.splits?.completed?.totalValue || 0,
      previousValue: null,
      formatType: 'currency',
      icon: 'bi-check-circle-fill',
      color: 'green',
      subtitle: `${financialFlow?.splits?.completed?.count || 0} completed`,
      metricId: 'splits-completed'
    },
    {
      id: 'splits-pending',
      title: 'Pending Splits',
      value: financialFlow?.splits?.pending?.totalValue || 0,
      previousValue: null,
      formatType: 'currency',
      icon: 'bi-clock-history',
      color: 'yellow',
      subtitle: `${financialFlow?.splits?.pending?.count || 0} pending`,
      metricId: 'splits-pending'
    },
    {
      id: 'requests-total',
      title: 'Payment Requests',
      value: financialFlow?.requests?.totalValue || 0,
      previousValue: null,
      formatType: 'currency',
      icon: 'bi-cash-coin',
      color: 'blue',
      subtitle: `${financialFlow?.requests?.count || 0} requests`,
      metricId: 'requests-total'
    },
    {
      id: 'shared-wallet-balance',
      title: 'Shared Wallet Balance',
      value: financialFlow?.sharedWallets?.totalBalance || walletData?.totalBalance || 0,
      previousValue: previousData.wallets?.totalBalance || null,
      formatType: 'currency',
      icon: 'bi-wallet2',
      color: 'teal',
      subtitle: `${financialFlow?.sharedWallets?.count || walletData?.totalWallets || 0} wallets`,
      metricId: 'shared-wallet-balance'
    },
    {
      id: 'user-wallet-flow',
      title: 'User Wallet Net Flow',
      value: financialFlow?.userWallets?.netFlow || 0,
      previousValue: null,
      formatType: 'currency',
      icon: 'bi-arrow-left-right',
      color: 'cyan',
      subtitle: `Deposits: $${formatNumber(financialFlow?.userWallets?.totalDeposits || 0)}`,
      metricId: 'user-wallet-flow'
    }
  ], [financialFlow, splitData, walletData, previousData, period, formatNumber]);

  // Engagement Metrics with trends
  const engagementMetrics = useMemo(() => [
    {
      id: 'badge-claims',
      title: 'Badge Claims',
      value: badgeData?.totalBadgeClaims || 0,
      previousValue: previousData.badges?.totalBadgeClaims || null,
      formatType: 'number',
      icon: 'bi-award',
      color: 'yellow',
      subtitle: 'Total claims',
      metricId: 'badge-claims'
    },
    {
      id: 'users-with-badges',
      title: 'Users with Badges',
      value: badgeData?.usersWithBadges || 0,
      previousValue: previousData.badges?.usersWithBadges || null,
      formatType: 'number',
      icon: 'bi-star-fill',
      color: 'orange',
      subtitle: formatPercent(badgeData?.badgeAdoptionRate || 0, 1),
      metricId: 'users-with-badges'
    },
    {
      id: 'phone-verified',
      title: 'Phone Verified',
      value: phoneAuthData?.usersWithVerifiedPhone || 0,
      previousValue: previousData.phoneAuth?.usersWithVerifiedPhone || null,
      formatType: 'number',
      icon: 'bi-shield-check',
      color: 'blue',
      subtitle: formatPercent(phoneAuthData?.phoneVerificationRate || 0, 1),
      metricId: 'phone-verified'
    },
    {
      id: 'shared-wallets',
      title: 'Shared Wallets',
      value: walletData?.totalWallets || 0,
      previousValue: previousData.wallets?.totalWallets || null,
      formatType: 'number',
      icon: 'bi-wallet-fill',
      color: 'teal',
      subtitle: 'Active wallets',
      metricId: 'shared-wallets'
    }
  ], [badgeData, phoneAuthData, walletData, previousData]);

  // Split Activity Metrics with trends - Reordered by importance
  const splitActivityMetrics = useMemo(() => [
    {
      id: 'active-splits',
      title: 'Active Splits',
      value: splitData?.activeSplits || 0,
      previousValue: previousData.splits?.activeSplits || null,
      formatType: 'number',
      icon: 'bi-check-circle',
      color: 'green',
      subtitle: 'Currently active',
      metricId: 'active-splits'
    },
    {
      id: 'completed-splits',
      title: 'Completed Splits',
      value: splitData?.completedSplits || 0,
      previousValue: previousData.splits?.completedSplits || null,
      formatType: 'number',
      icon: 'bi-check-circle-fill',
      color: 'teal',
      subtitle: 'Finished',
      metricId: 'completed-splits'
    },
    {
      id: 'degen-splits',
      title: 'Degen Splits',
      value: splitData?.degenSplits || 0,
      previousValue: previousData.splits?.degenSplits || null,
      formatType: 'number',
      icon: 'bi-dice-5',
      color: 'purple',
      subtitle: formatPercent(
        splitData?.totalSplits > 0 
          ? (splitData.degenSplits / splitData.totalSplits) * 100 
          : 0, 
        1
      ),
      metricId: 'degen-splits'
    },
    {
      id: 'avg-participants',
      title: 'Avg Participants',
      value: splitData?.averageParticipants || 0,
      previousValue: previousData.splits?.averageParticipants || null,
      formatType: 'number',
      icon: 'bi-people',
      color: 'indigo',
      subtitle: 'Per split',
      metricId: 'avg-participants'
    }
  ], [splitData, previousData]);

  return (
    <div className="dashboard-overview-enhanced">
      {/* Hero KPI Section with Trend Indicators */}
      <section className="overview-hero">
        <div className="hero-header">
          <div>
            <h1 className="overview-title">Dashboard Overview</h1>
            <p className="overview-subtitle">Key metrics at a glance • {period} period</p>
          </div>
          {previousData?.users && (
            <div className="period-comparison-badge">
              <i className="bi bi-graph-up"></i>
              <span>Comparing to previous {getPreviousPeriodLabel(period)}</span>
            </div>
          )}
        </div>
        <div className="kpi-grid-hero">
          {topKPIs.map(kpi => {
            const trend = calculateTrend(kpi.value, kpi.previousValue);
            return (
              <div key={kpi.id} className="kpi-card-wrapper">
                <KPICard
                  {...kpi}
                  trend={trend}
                  metricId={kpi.metricId || kpi.id}
                />
                {kpi.previousValue !== null && (
                  <TrendIndicator
                    value={kpi.value}
                    previousValue={kpi.previousValue}
                    formatType={kpi.formatType}
                    size="small"
                    showComparison={false}
                  />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Main Metrics Grid */}
      <div className="overview-grid">
        {/* Left Column */}
        <div className="overview-column">
          <MetricSection
            title="User Growth"
            icon="bi-people"
            description="User acquisition and engagement metrics"
            metrics={userMetrics.map(metric => ({
              ...metric,
              trend: calculateTrend(metric.value, metric.previousValue)
            }))}
            loading={userLoading}
          />
          
          <MetricSection
            title="Split Activity"
            icon="bi-scissors"
            description="Split creation and completion metrics"
            metrics={splitActivityMetrics.map(metric => ({
              ...metric,
              trend: calculateTrend(metric.value, metric.previousValue)
            }))}
            loading={splitLoading}
          />
        </div>

        {/* Right Column */}
        <div className="overview-column">
          <MetricSection
            title="Financial Metrics"
            icon="bi-wallet2"
            description="Revenue and transaction metrics"
            metrics={financialMetrics.map(metric => ({
              ...metric,
              trend: calculateTrend(metric.value, metric.previousValue)
            }))}
            loading={txLoading || walletLoading}
          />
          
          <MetricSection
            title="User Engagement"
            icon="bi-graph-up"
            description="Badges, authentication, and wallet usage"
            metrics={engagementMetrics.map(metric => ({
              ...metric,
              trend: calculateTrend(metric.value, metric.previousValue)
            }))}
            loading={badgeLoading || phoneAuthLoading}
          />
        </div>
      </div>

      {/* Report Export Section */}
      <section className="overview-report-section">
        <ReportExport
          userData={userData}
          splitData={splitData}
          txData={txData}
          walletData={walletData}
          badgeData={badgeData}
          phoneAuthData={phoneAuthData}
          financialFlow={financialFlow}
          period={period}
        />
      </section>
    </div>
  );
};

DashboardOverview.propTypes = {
  period: PropTypes.string.isRequired,
  platform: PropTypes.string,
  compact: PropTypes.bool
};

DashboardOverview.defaultProps = {
  platform: null,
  compact: false
};

export default React.memo(DashboardOverview);

